import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChevronIcon } from '~/assets';
import SecondaryButton from '~/components/Button/SecondaryButton';
import Checkbox from '~/components/Checkbox';
import MatchingAttributePicker from '~/components/MatchingAttributePicker';
import { CustomerListMatchingAttribute } from '~/types/api.graphql';
import {
  CustomerListColumn,
  CustomerListColumnTypes,
  CustomerListMatchingLimits,
  CustomerListRuleDraft,
  MatchingRulesIssue,
} from '~/utils/customerListMatchingRules';

import { ErrorLine } from '../visitiqCatalog.styles';
import { describeMatchingRulesIssue } from './issueMessages';
import {
  Badge,
  ColumnName,
  ColumnRow,
  FieldContainer,
  HelperText,
  IconButton,
  RuleCard,
  RuleHeader,
  Subtitle,
  Title,
  TypePicker,
} from './MatchingRulesField.styles';
import ProfileAttributeWarning from './ProfileAttributeWarning';
import RuleTitle from './RuleTitle';

interface SimpleViewProps {
  label?: string;
  columns: CustomerListColumn[];
  limits: CustomerListMatchingLimits;
  types: CustomerListColumnTypes;
  rules: CustomerListRuleDraft[];
  issues: MatchingRulesIssue[];
  changedColumns: Set<number>;
  warningColumn: number | null;
  onTypeChange: (columnIndex: number, attribute: CustomerListMatchingAttribute | undefined) => void;
  onToggleColumn: (columnIndex: number) => void;
  onConfirmWarning: () => void;
  onCancelWarning: () => void;
  onCustomize: () => void;
}

/**
 * One **Any of** section: every column with a checkbox and its type picker, ticked or not, so the user sees every type
 * the file could match on; each ticked column is a rule of its own.
 */
const SimpleView = ({
  label,
  columns,
  limits,
  types,
  rules,
  issues,
  changedColumns,
  warningColumn,
  onTypeChange,
  onToggleColumn,
  onConfirmWarning,
  onCancelWarning,
  onCustomize,
}: SimpleViewProps): JSX.Element => {
  const { t } = useTranslation();
  const [isExpanded, setExpanded] = useState(true);
  const tickedColumns = rules.flat();
  const tickedFields = columns
    .filter(({ index }) => tickedColumns.includes(index))
    .map(({ index, columnName }): [number, string, CustomerListMatchingAttribute | undefined] => [
      index,
      columnName,
      types[index],
    ]);
  const warnedColumn = columns.find(({ index }) => index === warningColumn);
  const warnedAttribute = warningColumn === null ? undefined : types[warningColumn];

  return (
    <FieldContainer>
      <div>
        <Title>{label ?? t('aiInsights.a2ui.matchingRules.title')}</Title>
        <Subtitle>{t('aiInsights.a2ui.matchingRules.subtitle')}</Subtitle>
      </div>
      <RuleCard>
        <RuleHeader>
          <IconButton
            type="button"
            aria-label={t('aiInsights.a2ui.matchingRules.expandAnyOf')}
            aria-expanded={isExpanded}
            $rotated={isExpanded}
            onClick={() => setExpanded(!isExpanded)}
          >
            <ChevronIcon />
          </IconButton>
          <RuleTitle anyOf fields={tickedFields} />
        </RuleHeader>
        {isExpanded && (
          <>
            <HelperText>{t('aiInsights.a2ui.matchingRules.whichColumnsAlone')}</HelperText>
            {columns.map(({ index, columnName, suggestedAttribute }) => {
              const attribute = types[index];
              const isTicked = tickedColumns.includes(index);
              const isSuggested =
                !!suggestedAttribute && attribute === suggestedAttribute && !changedColumns.has(index);
              return (
                <ColumnRow key={index} $highlighted={isTicked}>
                  <Checkbox aria-label={columnName} isSelected={isTicked} onChange={() => onToggleColumn(index)} />
                  <ColumnName>{columnName}</ColumnName>
                  {isSuggested && <Badge>{t('aiInsights.a2ui.matchingRules.suggested')}</Badge>}
                  <TypePicker>
                    <MatchingAttributePicker
                      allowNone
                      attribute={attribute}
                      identifierAttributes={limits.identifierAttributes}
                      onChange={(selected) => onTypeChange(index, selected)}
                    />
                  </TypePicker>
                </ColumnRow>
              );
            })}
          </>
        )}
        {/* Each ticked column is a rule, so one problem can come once per column; it is said once */}
        {issues
          .filter((issue, issueIndex) => issues.findIndex(({ problem }) => problem === issue.problem) === issueIndex)
          .map((issue) => (
            <ErrorLine key={issue.problem}>{describeMatchingRulesIssue({ t, issue, columns, limits })}</ErrorLine>
          ))}
      </RuleCard>
      <SecondaryButton auto disableMinWidth css={{ width: 'fit-content' }} onPress={onCustomize}>
        {t('aiInsights.a2ui.matchingRules.customize')}
      </SecondaryButton>
      {warnedColumn && warnedAttribute && (
        <ProfileAttributeWarning
          columnName={warnedColumn.columnName}
          attribute={warnedAttribute}
          onConfirm={onConfirmWarning}
          onCancel={onCancelWarning}
        />
      )}
    </FieldContainer>
  );
};

export default SimpleView;
